import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ScrollText, Filter } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  trade:     { label: "Trade",      color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  injured:   { label: "Injured",    color: "bg-red-500/20 text-red-400 border-red-500/30" },
  activated: { label: "Activated",  color: "bg-green-500/20 text-green-400 border-green-500/30" },
  rehab:     { label: "Rehab",      color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  optioned:  { label: "Optioned",   color: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
  recalled:  { label: "Recalled",   color: "bg-teal-500/20 text-teal-400 border-teal-500/30" },
  dfa:       { label: "DFA",        color: "bg-red-700/20 text-red-300 border-red-700/30" },
  signed:    { label: "Signed",     color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  selected:  { label: "Selected",   color: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30" },
  released:  { label: "Released",   color: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
  other:     { label: "Other",      color: "bg-astros-border/30 text-astros-muted border-astros-border" },
};

const FILTER_OPTIONS = [
  { key: "all", label: "ALL" },
  { key: "trade", label: "TRADES" },
  { key: "injured", label: "IL" },
  { key: "activated", label: "ACTIVATED" },
  { key: "roster", label: "ROSTER" },
  { key: "signed", label: "SIGNED" },
];

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function groupByMonth(txns) {
  const groups = {};
  txns.forEach(t => {
    const d = new Date(t.date + "T12:00:00");
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    if (!groups[key]) groups[key] = { label, txns: [] };
    groups[key].txns.push(t);
  });
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

export default function TransactionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/transactions`);
        setData(res.data);
      } catch (e) {
        console.error("Error fetching transactions:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const txns = data?.transactions || [];
  const filtered = filter === "all" ? txns
    : filter === "roster" ? txns.filter(t => ["optioned", "recalled", "selected", "dfa", "released"].includes(t.category))
    : txns.filter(t => t.category === filter);

  const months = groupByMonth(filtered);

  // Count by category for badges
  const counts = {};
  txns.forEach(t => { counts[t.category] = (counts[t.category] || 0) + 1; });

  return (
    <div data-testid="transactions-tab">
      <div className="flex items-center gap-3 mb-2">
        <ScrollText className="w-6 h-6 text-astros-orange" />
        <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white">TRANSACTIONS</h2>
      </div>
      <p className="text-astros-muted text-sm mb-6">
        2026 Houston Astros roster moves, trades, signings, and injury updates.
      </p>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 mb-6">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => setFilter(opt.key)}
            className={`px-3 py-1.5 font-bebas text-sm tracking-wide transition-colors border ${
              filter === opt.key
                ? "bg-astros-orange/20 border-astros-orange text-astros-orange"
                : "border-astros-border text-astros-muted hover:text-white hover:border-white/30"
            }`}
            data-testid={`txn-filter-${opt.key}`}
          >
            {opt.label}
            {opt.key !== "all" && opt.key !== "roster" && counts[opt.key] > 0 && (
              <span className="ml-1.5 text-[10px] opacity-60">{counts[opt.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Grouped by month */}
      <div className="space-y-6">
        {months.map(([key, group]) => (
          <section key={key}>
            <h3 className="font-bebas text-lg text-astros-muted tracking-wide mb-2 border-b border-astros-border/30 pb-1">
              {group.label} <span className="text-xs font-ibm-plex font-normal">({group.txns.length})</span>
            </h3>
            <div className="space-y-1">
              {group.txns.map((txn, idx) => {
                const config = CATEGORY_CONFIG[txn.category] || CATEGORY_CONFIG.other;
                return (
                  <motion.div
                    key={txn.id || idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.01 }}
                    className="flex items-start gap-3 py-2 px-2 hover:bg-astros-border/10 transition-colors"
                    data-testid={`txn-${txn.id || idx}`}
                  >
                    <span className="text-xs text-astros-muted font-mono min-w-[55px] pt-0.5">
                      {formatDate(txn.date)}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 border min-w-[70px] text-center ${config.color}`}>
                      {config.label}
                    </span>
                    <p className="text-sm text-white/90 flex-1 leading-relaxed">{txn.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-astros-muted text-center py-8">No transactions found for this filter</p>
      )}

      <p className="text-astros-muted/50 text-xs text-center mt-6">
        Showing {filtered.length} of {txns.length} total transactions
      </p>
    </div>
  );
}
